import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction } from "@impero-com/common/utils/i18n";
import moment from "moment-timezone";

import {
    ControlResultCoveredPeriod,
    ControlResultFinancialYear,
} from "api/generated/model";
import { formatPeriodLabel } from "org/control/view/utils/formatPeriodStrings";

const COVERED_PERIOD_UNIT_TO_TECH_PREFIX = {
    Month: "M",
    TwoMonths: "T",
    Quarter: "Q",
    Half: "H",
    Year: "Y",
} as const;

// Financial year can be only hit when we set the covered period to either: Quarter, Half year or Year
export function buildCoveredPeriodTechString(
    period: ControlResultCoveredPeriod,
    financialYear: ControlResultFinancialYear | null
): string {
    const calendarYear =
        period.unit === "Year" ? period.value : moment(period.start).year();
    const yearPart = financialYear
        ? `FY ${formatFinancialYearRange(financialYear)}` // here we should never hit the Month and TwoMonths
        : `${calendarYear}`;
    if (period.unit === "Year") {
        // A financial year standing as the period itself carries no unit: v1
        // and the report both write `FY 2025-2026`, not `Y FY 2025-2026`.
        return financialYear ? yearPart : `Y ${yearPart}`;
    }
    return `${COVERED_PERIOD_UNIT_TO_TECH_PREFIX[period.unit]}${period.value} ${yearPart}`;
}

function formatFinancialYearRange({
    start,
    end,
}: ControlResultFinancialYear): string {
    const startYear = moment(start).year();
    const endYear = moment(end).year();
    return startYear !== endYear ? `${startYear}-${endYear}` : `${startYear}`;
}

export function formatCoveredPeriod(
    t: TFunction,
    coveredPeriod: ControlResultCoveredPeriod | null,
    financialYear: ControlResultFinancialYear | null
): string | undefined {
    if (!coveredPeriod) {
        return undefined;
    }
    const techString = buildCoveredPeriodTechString(
        coveredPeriod,
        financialYear
    );
    return formatPeriodLabel(t, techString);
}

/**
 * Order by when the period ends (earlier first). When two periods end on the
 * same day, the one that starts later comes first — so a month is listed
 * before the quarter that ends with it (e.g. "March 2026" before "Q1 2026").
 */
export const compareCoveredPeriods: Cmp.ComparisonFunction<ControlResultCoveredPeriod | null> =
    Cmp.undefinedLast(
        Cmp.chain<ControlResultCoveredPeriod>([
            Cmp.map((p) => p.end),
            Cmp.inverse(Cmp.map((p) => p.start)),
        ])
    );

export function buildSortedPeriodFilterOptions(
    labelledPeriods: Array<{
        techString: string;
        label: string;
        period: ControlResultCoveredPeriod | null;
    }>
): Array<{ label: string; value: string }> {
    // Remove duplicates by techString
    const byTechString = new Map(
        labelledPeriods
            .filter(({ label }) => label !== "")
            .map((entry) => [entry.techString, entry])
    );
    return byTechString
        .values()
        .toArray()
        .sort((a, b) => compareCoveredPeriods(a.period, b.period))
        .map(({ techString, label }) => ({ label, value: techString }));
}
